import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useBeforeUnload } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';
import HistoryModal from '../components/HistoryModal';
import PatientInfoCard from '../components/provider/PatientInfoCard';
import MembershipStatusCard from '../components/provider/MembershipStatusCard';
import CareTeamSection from '../components/provider/CareTeamSection';
import ProviderNotesSection from '../components/provider/ProviderNotesSection';
import VisitLogSection from '../components/provider/VisitLogSection';
import HealthGoalsSection from '../components/provider/HealthGoalsSection';
import ProblemListSection from '../components/provider/ProblemListSection';
import ClinicalHistorySection from '../components/provider/ClinicalHistorySection';
import LifestyleSummarySection from '../components/provider/LifestyleSummarySection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderPatientEdit = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [historyModal, setHistoryModal] = useState({ open: false, section: '', label: '' });
  const [editingSections, setEditingSections] = useState({
    careTeam: false,
    healthGoals: false,
    problemList: false,
    clinicalHistory: false,
    lifestyleSummary: false,
    providerNotes: false
  });
  const [collapsedSections, setCollapsedSections] = useState({
    clinicalHistory: false,
    lifestyleSummary: false
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const fetchPatient = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPatient(response.data);
      setEditedData(response.data);
    } catch (error) {
      // console.error('Error fetching patient:', error);
      toast.error('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchPatient();
  }, [fetchPatient]);

  // Track unsaved changes
  useEffect(() => {
    const isEditing = Object.values(editingSections).some(val => val === true);
    const hasChanges = isEditing && JSON.stringify(editedData) !== JSON.stringify(patient);
    setHasUnsavedChanges(hasChanges);
  }, [editingSections, editedData, patient]);

  // Warn user about unsaved changes when leaving page
  useBeforeUnload(
    useCallback(() => {
      if (hasUnsavedChanges) {
        return "You have unsaved changes. Are you sure you want to leave?";
      }
    }, [hasUnsavedChanges])
  );

  // Block navigation within the app
  useEffect(() => {
    const handleNavigation = (e) => {
      if (hasUnsavedChanges) {
        const message = "You have unsaved changes. Are you sure you want to leave this page?";
        if (!window.confirm(message)) {
          e.preventDefault();
        }
      }
    };
    const links = document.querySelectorAll('a[href]');
    links.forEach(link => link.addEventListener('click', handleNavigation));
    return () => {
      links.forEach(link => link.removeEventListener('click', handleNavigation));
    };
  }, [hasUnsavedChanges]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/patients/${patientId}`,
        editedData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Patient record updated successfully!');
      setHasUnsavedChanges(false);
      setEditingSections({
        careTeam: false,
        healthGoals: false,
        problemList: false,
        clinicalHistory: false,
        lifestyleSummary: false,
        providerNotes: false
      });
      fetchPatient();
    } catch (error) {
      // console.error('Error updating patient:', error);
      toast.error('Failed to update patient record');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleCareTeamChange = (updatedTeam) => {
    setEditedData(prev => ({ ...prev, care_team: updatedTeam }));
  };

  const addHealthGoal = () => {
    setEditedData(prev => ({
      ...prev,
      health_goals: [
        ...(prev.health_goals || []),
        { id: Date.now().toString(), category: '', description: '' }
      ]
    }));
  };

  const updateHealthGoal = (index, field, value) => {
    setEditedData(prev => {
      const goals = [...prev.health_goals];
      goals[index] = { ...goals[index], [field]: value };
      return { ...prev, health_goals: goals };
    });
  };

  const removeHealthGoal = (index) => {
    setEditedData(prev => ({
      ...prev,
      health_goals: prev.health_goals.filter((_, i) => i !== index)
    }));
  };

  const toggleEditSection = (section) => {
    if (hasUnsavedChanges && !editingSections[section]) {
      const confirmSwitch = window.confirm(
        "You have unsaved changes in another section. Are you sure you want to switch? Your unsaved changes will be lost."
      );
      if (!confirmSwitch) return;
      setEditedData(patient);
    }
    setEditingSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const cancelEdit = (section) => {
    setEditedData(patient);
    setEditingSections(prev => ({ ...prev, [section]: false }));
  };

  const toggleCollapse = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading patient...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Patient not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Link to="/provider/patients">
            <Button className="btn-ghost" data-testid="back-to-patients-btn">
              <ArrowLeft size={18} className="mr-2" />
              Back to My Patients
            </Button>
          </Link>
          <Link to={`/provider/patient/${patientId}/view`}>
            <Button className="btn-ghost" data-testid="view-as-patient-btn">
              <Eye size={18} className="mr-2" />
              View as Patient
            </Button>
          </Link>
        </div>

        <h1 className="text-3xl font-bold text-slate-900 mb-6">Update Patient Record</h1>

        <PatientInfoCard patient={patient} />
        <MembershipStatusCard membershipStatus={patient.membership_status} />

        <CareTeamSection
          editedData={editedData}
          isEditing={editingSections.careTeam}
          onToggleEdit={() => toggleEditSection('careTeam')}
          onCancelEdit={() => cancelEdit('careTeam')}
          onCareTeamChange={handleCareTeamChange}
        />

        <ProviderNotesSection
          editedData={editedData}
          isEditing={editingSections.providerNotes}
          onToggleEdit={() => toggleEditSection('providerNotes')}
          onCancelEdit={() => cancelEdit('providerNotes')}
          onUpdateField={updateField}
        />

        <VisitLogSection patientId={patientId} />

        <HealthGoalsSection
          editedData={editedData}
          isEditing={editingSections.healthGoals}
          onToggleEdit={() => toggleEditSection('healthGoals')}
          onCancelEdit={() => cancelEdit('healthGoals')}
          onAddGoal={addHealthGoal}
          onUpdateGoal={updateHealthGoal}
          onRemoveGoal={removeHealthGoal}
        />

        <ProblemListSection
          editedData={editedData}
          isEditing={editingSections.problemList}
          onToggleEdit={() => toggleEditSection('problemList')}
          onCancelEdit={() => cancelEdit('problemList')}
          onUpdateField={updateField}
        />

        <ClinicalHistorySection
          editedData={editedData}
          isEditing={editingSections.clinicalHistory}
          isCollapsed={collapsedSections.clinicalHistory}
          onToggleEdit={() => toggleEditSection('clinicalHistory')}
          onCancelEdit={() => cancelEdit('clinicalHistory')}
          onToggleCollapse={() => toggleCollapse('clinicalHistory')}
          onUpdateField={updateField}
        />

        <LifestyleSummarySection
          editedData={editedData}
          isEditing={editingSections.lifestyleSummary}
          isCollapsed={collapsedSections.lifestyleSummary}
          onToggleEdit={() => toggleEditSection('lifestyleSummary')}
          onCancelEdit={() => cancelEdit('lifestyleSummary')}
          onToggleCollapse={() => toggleCollapse('lifestyleSummary')}
          onUpdateField={updateField}
        />

        {/* Save Button Footer */}
        <div className="flex justify-end mt-8 mb-8">
          <Button onClick={handleSave} disabled={saving} className="btn-primary text-lg px-8 py-3" data-testid="save-all-changes-btn">
            <Save size={20} className="mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>

      {/* History Modal */}
      <HistoryModal
        open={historyModal.open}
        patientId={patientId}
        sectionName={historyModal.section}
        sectionLabel={historyModal.label}
        onClose={() => setHistoryModal({ open: false, section: '', label: '' })}
      />
    </div>
  );
};

export default ProviderPatientEdit;
